import React, { forwardRef, useState } from 'react'
import { ThemeProvider } from 'styled-components'

import { formatCoversPerUnit, formatRecipeSlug } from '../../helpers'
import { useTranslation } from '../../contexts/I18nContext'
import WaveOverlay from '../WaveOverlay'

import Styled from './UserGeneratedRecipeCard.styled'

/**
 * Uploaded recipe card component
 * @param {Object} author
 * @param {string} backgroundColor
 * @param {string} backgroundPattern
 * @param {JSX.Element} children
 * @param {string} className
 * @param {number} coversCount
 * @param {string} description
 * @param {'vertical'|'horizontal'} direction
 * @param {string} href
 * @param {string} id
 * @param {string} imageUrl
 * @param {boolean} isCooked
 * @param {boolean} isLoading
 * @param {boolean} isRecipeAuthor
 * @param {[]} missingIngredients
 * @param {string} oldRecipeId
 * @param {function} onClick
 * @param {function} openModal
 * @param {string} placeHolderUrl
 * @param {boolean} prefetch
 * @param {boolean} preview
 * @param {number} roundedCoversCount
 * @param {Object} style
 * @param {Array<string>} tagsEdito
 * @param {string} target
 * @param {JSX.Element} thumbnailOverlayChildren
 * @param {string} title
 * @param {string} unitName
 * @param {'main'|'profile'|'embed'|'drawer'} variant
 */
const UserGeneratedRecipeCard = forwardRef(
  (
    {
      addedToMenu,
      author,
      backgroundPattern,
      children,
      className,
      coversCount,
      description,
      direction,
      href,
      id,
      imageUrl,
      isCooked,
      isLoading,
      isRecipeAuthor,
      missingIngredients,
      oldRecipeId,
      onAnimationEnd,
      onAnimationStart,
      onClick,

      openModal,
      placeHolderUrl,
      prefetch,
      preview,
      pricePerPortionTag,
      roundedCoversCount,
      style,
      tagsEdito,
      target,
      thumbnailOverlayChildren,
      title,
      unitName,
      variant,
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const [imageLoaded, setImageLoaded] = useState(false)
    return (
      <ThemeProvider theme={{ variant, direction }}>
        <Styled
          ref={ref}
          className={className}
          onAnimationEnd={onAnimationEnd}
          onAnimationStart={onAnimationStart}
          style={style}
        >
          <Styled.container isLoading={isLoading} isCooked={isCooked}>
            <Styled.wrapper
              isCooked={isCooked}
              to={
                id && !isLoading
                  ? `/user-recipes/${formatRecipeSlug({
                      id,
                      coversCount,
                      preview,
                      oldRecipeId,
                      from: addedToMenu ? 'menu' : '',
                    })}`
                  : undefined
              }
              href={href}
              target={target}
              onClick={onClick}
              prefetch={prefetch}
            >
              <Styled.content>
                <Styled.thumbnailPreserveSpace>
                  <Styled.thumbnailPreserveSpaceContainer>
                    <Styled.thumbnailContainer
                      imageLoaded={imageLoaded}
                      backgroundPatternUrl={
                        imageUrl && imageLoaded
                          ? ''
                          : backgroundPattern?.imageUrl
                      }
                    >
                      {(imageUrl || placeHolderUrl) && !isLoading ? (
                        <Styled.thumbnail>
                          <Styled.thumbnailWrapper
                            imageLoaded={imageLoaded}
                            isCooked={typeof isCooked === 'boolean' && isCooked}
                            isPlaceholder={!imageUrl && placeHolderUrl}
                          >
                            <Styled.imageWithBlurryPlaceholder
                              alt={title}
                              placeholderColor={backgroundPattern?.color}
                              onLoad={() => setImageLoaded(true)}
                              placeholder="blur"
                              sizes="400px"
                              shape="rectangular"
                              objectFit={
                                !imageUrl && placeHolderUrl
                                  ? 'contain'
                                  : 'cover'
                              }
                              src={
                                !imageUrl && placeHolderUrl
                                  ? placeHolderUrl
                                  : imageUrl
                              }
                              title={title}
                            />
                          </Styled.thumbnailWrapper>
                        </Styled.thumbnail>
                      ) : null}

                      {thumbnailOverlayChildren ? (
                        <Styled.thumbnailLayer>
                          <Styled.thumbnailLayerContent>
                            {thumbnailOverlayChildren}
                          </Styled.thumbnailLayerContent>
                        </Styled.thumbnailLayer>
                      ) : null}

                      {pricePerPortionTag ? (
                        <Styled.pricePerPortionBadge
                          level={pricePerPortionTag.level}
                          currency={pricePerPortionTag.currency}
                          label={pricePerPortionTag.label}
                          isFull={false}
                        />
                      ) : null}
                    </Styled.thumbnailContainer>
                  </Styled.thumbnailPreserveSpaceContainer>
                </Styled.thumbnailPreserveSpace>
                <Styled.text isLoading={isLoading}>
                  {author?.name && !isRecipeAuthor ? (
                    <Styled.author
                      name={author?.name}
                      imageUrl={author?.imageUrl}
                    />
                  ) : null}
                  <Styled.title
                    isCooked={typeof isCooked === 'boolean' && isCooked}
                  >
                    {isLoading ? (
                      <>
                        <Styled.titlePlaceholder>
                          &nbsp;
                        </Styled.titlePlaceholder>
                        <br />
                        <Styled.titlePlaceholder>
                          &nbsp;
                        </Styled.titlePlaceholder>
                      </>
                    ) : (
                      title
                    )}
                  </Styled.title>
                  <Styled.description>
                    {isLoading ? (
                      <>
                        <Styled.descriptionPlaceholder>
                          &nbsp;
                        </Styled.descriptionPlaceholder>
                        <br />
                        <Styled.descriptionPlaceholder>
                          &nbsp;
                        </Styled.descriptionPlaceholder>
                      </>
                    ) : (
                      description
                    )}
                  </Styled.description>
                  {!isLoading &&
                  missingIngredients &&
                  missingIngredients.length &&
                  openModal ? (
                    <Styled.missingIngredients
                      onClick={(e) => {
                        e.preventDefault()
                        openModal('MissingIngredients', {
                          ingredients: missingIngredients,
                        })
                      }}
                    >
                      {t(
                        'RECIPE.MISSING_INGREDIENTS',
                        missingIngredients.length,
                      )}
                    </Styled.missingIngredients>
                  ) : null}
                  <Styled.labels>
                    {tagsEdito && tagsEdito.length && !isLoading
                      ? tagsEdito.map((tag) => (
                          <Styled.badgeEdito key={tag}>{tag}</Styled.badgeEdito>
                        ))
                      : null}
                    {roundedCoversCount > 1 && unitName && !isLoading ? (
                      <Styled.tag>
                        {formatCoversPerUnit({
                          coversCount,
                          unitName,
                          roundedCoversCount,
                        })}
                      </Styled.tag>
                    ) : null}
                  </Styled.labels>
                </Styled.text>
              </Styled.content>
            </Styled.wrapper>
            {children}
          </Styled.container>

          {isLoading ? <WaveOverlay /> : null}
        </Styled>
      </ThemeProvider>
    )
  },
)

UserGeneratedRecipeCard.displayName = 'UserGeneratedRecipeCard'

UserGeneratedRecipeCard.defaultProps = {
  children: null,
  coversCount: 1,
  direction: 'vertical',
  href: undefined,
  imageUrl: '',
  isLoading: false,
  missingIngredients: [],
  preview: false,
  tagsEdito: [],
  thumbnailOverlayChildren: null,
  variant: 'main',
}

export default UserGeneratedRecipeCard
